import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
} from "aws-amplify/auth";

import {
  buildTokenInfo,
  buildUserPayload,
  safeParseAuthOperation,
} from "./amplify.helpers";
import type {
  GetAuthMethodParameters,
  IsomorphicAuthObject,
} from "./amplify.types";

// The same unsigned request Amplify sends; the access token is the only
// credential. The region is the pool id's prefix (us-west-1_xxxx).
const globalSignOut = async (accessToken: string) => {
  const region = String(import.meta.env.VITE_COGNITO_USER_POOL_ID).split(
    "_",
  )[0];
  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.GlobalSignOut",
    },
    body: JSON.stringify({ AccessToken: accessToken }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(
      body.message ?? `GlobalSignOut failed (${response.status})`,
    ) as Error & { code: string };
    error.code = body.__type ?? "GlobalSignOutFailed";
    throw error;
  }
};

class IsomorphicAuth implements IsomorphicAuthObject {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
          userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        },
      },
    });
  }

  async updatePassword(...params: GetAuthMethodParameters<"updatePassword">) {
    return safeParseAuthOperation(async () => await updatePassword(...params));
  }

  configure(...params: GetAuthMethodParameters<"configure">) {
    return Amplify.configure(...params);
  }

  async confirmSignIn(...params: GetAuthMethodParameters<"confirmSignIn">) {
    return safeParseAuthOperation(async () => await confirmSignIn(...params));
  }

  async currentAuthenticatedUser() {
    return safeParseAuthOperation(async () => {
      const user = await getCurrentUser();
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken;
      const accessToken = tokens?.accessToken;

      return {
        authenticateUser: buildUserPayload({ user, tokens }),
        idToken: buildTokenInfo(idToken),
        accessToken: buildTokenInfo(accessToken),
      };
    });
  }

  async signIn(...params: GetAuthMethodParameters<"signIn">) {
    return safeParseAuthOperation(async () => await signIn(...params));
  }

  async signUp(...params: GetAuthMethodParameters<"signUp">) {
    return safeParseAuthOperation(async () => await signUp(...params));
  }

  async signOut() {
    return safeParseAuthOperation(async () => await signOut());
  }

  // Two-factor sign-in (OMG-2777): an authenticator app (TOTP) per admin.
  async fetchMfaPreference() {
    return safeParseAuthOperation(async () => await fetchMFAPreference());
  }

  async setUpTotp() {
    return safeParseAuthOperation(async () => await setUpTOTP());
  }

  // Proves the authenticator works. Two-factor is not on until preferTotp.
  async verifyTotpSetup(code: string) {
    return safeParseAuthOperation(async () => await verifyTOTPSetup({ code }));
  }

  // Separate from verifyTotpSetup so a failure here is not reported as a
  // wrong code, and a retry does not need a new one.
  async preferTotp() {
    return safeParseAuthOperation(
      async () => await updateMFAPreference({ totp: "PREFERRED" }),
    );
  }

  // Revokes every refresh token for the account, so a session started before
  // two-factor was turned on cannot outlive it. Calls Cognito's GlobalSignOut
  // itself: Amplify's signOut({ global: true }) swallows a failed request and
  // resolves anyway, so its result cannot say whether other sessions are gone.
  // Local tokens are cleared either way.
  async signOutEverywhere() {
    return safeParseAuthOperation(async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const accessToken = tokens?.accessToken?.toString();
        if (!accessToken) throw new Error("No session to sign out");
        await globalSignOut(accessToken);
      } finally {
        await signOut().catch(() => undefined);
      }
    });
  }

  async resetPassword(...params: GetAuthMethodParameters<"resetPassword">) {
    return safeParseAuthOperation(async () => await resetPassword(...params));
  }

  async confirmResetPassword(
    ...params: GetAuthMethodParameters<"confirmResetPassword">
  ) {
    return safeParseAuthOperation(
      async () => await confirmResetPassword(...params),
    );
  }
}

export const Auth = new IsomorphicAuth();
